import { QUERY_RESULT_LIMIT } from "csi/constants";
import { C8Node, C8NodeType } from "csi/typings";
import { action, makeObservable, observable } from "mobx";

interface ISessionConstants {
	admin: boolean;
	allow3d: boolean;
	allow3dAnnotations: boolean;
	clarityId: string;
	configEditor: boolean;
	configViewer: boolean;
	customRequestHeaders: string[];
	datePattern: string;
	debugViewer: boolean;
	decimalSeparator: string;
	dev: boolean;
	enableBreadcrumbSearch: boolean;
	excludedFileExtensions: string[];
	exemptedFileExtensions: string[];
	froalaLicense: string;
	fusionChartsLicense: string;
	groupSeparator: string;
	gtmId: string;
	idleExpirationTime: number;
	isAgent: boolean;
	isAsperaEnabled: boolean;
	isDriveEnabled: boolean;
	isMSTeamsEnabled: boolean;
	isSlackEnabled: boolean;
	isTextFlipHorizontalAllowed: boolean;
	keycloakEnabled: boolean;
	labelTemplateTypes: C8NodeType[];
	maxResultSize: number;
	msTeamsUserId: string;
	proofingSupportedFileTypes: string[];
	roleName: string;
	roleUrl: string;
	serviceAccountAuthenticationTypesEnabled: string;
	sessionUrl: string;
	setupViewer: boolean;
	showUserNameOnHeader: boolean;
	siteLocale: string;
	siteMaxCompositionColumns: number;
	siteMaxExportSize: number;
	sitePaperOrientation: string;
	sitePaperSize: string;
	slackBotUserId: string;
	slackUserId: string;
	serverTimeZoneOffset: number;
	timePattern: string;
	userAuthenticationTypesEnabled: string;
	userCompanyUrl: string;
	userCompanyInfoUrl: string;
	userDefaultNodes: C8Node[];
	userLicense: string[];
	userLocale: string;
	userSupplierUrl: string;
	userType: string;
	userUrl: string;
	validIllustratorExtensions: string[];
}

class SessionConstants {
	@observable.ref private _values: ISessionConstants;

	/** indicates if user is in administrator group */
	get admin(): boolean {
		return !!this._values?.admin;
	}

	/** indicates if user allows 3d */
	get allow3d(): boolean {
		return !!this._values?.allow3d;
	}

	/** indicates if user allows 3d annotations */
	get allow3dAnnotations(): boolean {
		return !!this._values?.allow3dAnnotations;
	}

	/** indicates if user has permission to edit site configuration */
	get configEditor(): boolean {
		return !!this._values?.configEditor;
	}

	/** indicates if user has permission to view site configuration */
	get configViewer(): boolean {
		return !!this._values?.configViewer;
	}

	/** get custom headers to be sent with the request */
	get customRequestHeaders(): string[] {
		return this._values?.customRequestHeaders;
	}

	/** represents the Date format for the current locale */
	get datePattern(): string {
		return this._values?.datePattern;
	}

	/** indicates if user has permission to view debug tools and views */
	get debugViewer(): boolean {
		return !!this._values?.debugViewer;
	}

	/** represents decimal separator of the number for the current locale (i.e. ",", ".", " ") */
	get decimalSeparator(): string {
		return this._values?.decimalSeparator;
	}

	/** indicates if user is a developer */
	get dev(): boolean {
		return !!this._values?.dev;
	}

	/** indicates if context-sensitive search is allowed from the breadcrumb */
	get enableBreadcrumbSearch(): boolean {
		return !!this._values?.enableBreadcrumbSearch;
	}

	/** file extensions excluded from being uploaded */
	get excludedFileExtensions(): string[] {
		return this._values?.excludedFileExtensions;
	}

	/** file extensions allowed to be uploaded */
	get exemptedFileExtensions(): string[] {
		return this._values?.exemptedFileExtensions;
	}

	/** get the license of froala editor */
	get froalaLicense(): string {
		return this._values?.froalaLicense;
	}

	/** get the license of fusion charts */
	get fusionChartsLicense(): string {
		return this._values?.fusionChartsLicense;
	}

	/** get idle time in milliseconds before user is logged out for inactivity */
	get idleExpirationTime(): number | null {
		return this._values?.idleExpirationTime;
	}

	get hasFullAccessLicense(): boolean {
		return this._values?.userLicense.includes("Development");
	}

	get hasCloudGoodLicense(): boolean {
		return this._values?.userLicense.includes("Good");
	}

	get hasCloudBetterLicense(): boolean {
		return this._values?.userLicense.includes("Better");
	}

	get hasCloudBestLicense(): boolean {
		return this._values?.userLicense.includes("Best");
	}

	/** true if IBM Aspera download protocol API is enabled on the server. */
	get isAsperaEnabled(): boolean {
		return this._values?.isAsperaEnabled;
	}

	/** get if user is a Customer user */
	get isCustomerUser(): boolean {
		return this._values?.userType == "Customer";
	}

	/** get if user is a SalesDivision user */
	get isSalesDivisionUser(): boolean {
		return this._values?.userType == "SalesDivision";
	}

	/** get if user is a Supplier user */
	get isSupplierUser(): boolean {
		return this._values?.userType == "Supplier";
	}

	/** true if Centric Drive API is enabled on the server. */
	get isDriveEnabled(): boolean {
		return !!this._values?.isDriveEnabled;
	}

	/** indicates if Microsoft Teams is enabled */
	get isMSTeamsEnabled(): boolean {
		return !!this._values?.isMSTeamsEnabled;
	}

	/** indicates if Slack is enabled */
	get isSlackEnabled(): boolean {
		return !!this._values?.isSlackEnabled;
	}

	/** get if text flipping in Canvas at the base level text shapes is allowed */
	get isTextFlipHorizontalAllowed(): boolean {
		return this._values?.isTextFlipHorizontalAllowed;
	}

	/** indicates if user has keycloak enabled */
	get keycloakEnabled(): boolean {
		return !!this._values?.keycloakEnabled;
	}

	/** returns a list of the labelGenerationInforNodes */
	get labelTemplateTypes(): C8NodeType[] {
		return this._values?.labelTemplateTypes;
	}
	/** represents group separator of the number for the current locale (i.e. ",", ".", " ") */
	get groupSeparator(): string {
		return this._values?.groupSeparator;
	}

	/** get the microsoft clarity ID */
	get clarityId(): string {
		return this._values?.clarityId;
	}

	/** get the google tag manager ID */
	get gtmId(): string {
		return this._values?.gtmId;
	}

	/** true if userSupplierUrl is an agent for other suppliers */
	get isAgent(): boolean {
		return this._values?.isAgent;
	}

	/** User is local if it is owned by a crew */
	get isLocalUser(): boolean {
		return !!this.userCompanyUrl;
	}

	/** return true if user has portal UI */
	get isPortalUser(): boolean {
		const { userType } = this;
		return (userType == "Customer") || (userType == "SalesDivision");
	}

	/** get site limit to number of query results to display */
	get maxResultSize(): number {
		// NOTE - hard limit of 200-5000 to protect client from issuing large queries
		return Math.max(Math.min(this._values?.maxResultSize || 0, QUERY_RESULT_LIMIT), 200);
	}

	/** the MS Teams User Id if the user is authenticated against MSTeams */
	get msTeamsUserId(): string {
		return this._values?.msTeamsUserId;
	}

	/** get file types for which proofing is supported */
	get proofingSupportedFileTypes(): string[] {
		return this._values?.proofingSupportedFileTypes;
	}

	/** name of role being played by user */
	get roleName(): string {
		return this._values?.roleName;
	}

	/** URL of role being played by user */
	get roleUrl(): string {
		return this._values?.roleUrl;
	}

	/** get Auth types are enabled for interactive users */
	get serviceAccountAuthenticationTypesEnabled(): string {
		return this._values?.serviceAccountAuthenticationTypesEnabled;
	}

	/** get Auth types are enabled for service users */
	get userAuthenticationTypesEnabled(): string {
		return this._values?.userAuthenticationTypesEnabled;
	}

	/** session url */
	get sessionUrl(): string {
		return this._values?.sessionUrl;
	}

	/** indicates if user has permission to view setup page */
	get setupViewer(): boolean {
		return this._values?.setupViewer;
	}

	/** indicates if username icon be replaced with user name */
	get showUserNameOnHeader(): boolean {
		return this._values?.showUserNameOnHeader;
	}

	/** default locale for site */
	get siteLocale(): string {
		return this._values?.siteLocale;
	}

	/** get site max Composition column */
	get siteMaxCompositionColumns(): number {
		return this._values?.siteMaxCompositionColumns;
	}

	/** get site max table size for which an ad hoc export request is allowed */
	get siteMaxExportSize(): number {
		return this._values?.siteMaxExportSize;
	}

	/** get site default paper orientation */
	get sitePaperOrientation(): string {
		return this._values?.sitePaperOrientation;
	}

	/** get site default paper size */
	get sitePaperSize(): string {
		return this._values?.sitePaperSize;
	}

	/** get the UserId for the SlackBot */
	get slackBotUserId(): string {
		return this._values?.slackBotUserId;
	}

	/** get the Slack User's Id*/
	get slackUserId(): string {
		return this._values?.slackUserId;
	}

	/** indicates the offset for the given time zone of the user*/
	get serverTimeZoneOffset(): number {
		return this._values?.serverTimeZoneOffset;
	}

	/** represents the Time format for the current locale */
	get timePattern(): string {
		return this._values?.timePattern;
	}

	/** URL of node owning current user */
	get userCompanyUrl(): string {
		return this._values?.userCompanyUrl;
	}

	/** URL of CompanyInfo node for online company */
	get userCompanyInfoUrl(): string {
		return this._values?.userCompanyInfoUrl;
	}

	/** user default nodes in system */
	get userDefaultNodes(): C8Node[] {
		return this._values?.userDefaultNodes;
	}

	/** user locale */
	get userLocale(): string {
		return this._values?.userLocale;
	}

	/** URL of supplier associated with user, either as parent company when a portal user, or User.HomeSupplier is hybrid user */
	get userSupplierUrl(): string {
		return this._values?.userSupplierUrl;
	}

	/** owner type of current user */
	get userType(): string {
		return this._values?.userType;
	}

	/** URL of current user */
	get userUrl(): string {
		return this._values?.userUrl;
	}

	/**  return array of file extensions supported by Illustrator */
	get validIllustratorExtensions(): string[] {
		return this._values?.validIllustratorExtensions;
	}

	/** return values object which allows for mocking and replacing values for testing */
	getValues = (): ISessionConstants => {
		return this._values;
	};

	/** Initialize the session constants */
	@action initialize = (values: ISessionConstants): void => {
		this._values = values;
	};

	constructor() {
		makeObservable(this);
	}
};

const sessionConstants = new SessionConstants();

export = sessionConstants;
